import React from "react"
import {ClickAwayListener, Typography} from "@mui/material"
import {UxCTA as CTA} from "@next/ux-cta"
import {formatTextTestIds} from "@next/utils"
import {breakpoints} from "@next/ux-fabric"
import {useMediaQuery} from "../../../../hooks/useMediaQuery"
import CountrySelectDropdown from "../../../CountrySelectDropdown"
import CountrySelectLanguageDropdown from "../../../CountrySelectLanguageDropdown"
import {CountryLanguage} from "../../../../models/countryselector"
import {CHEVRON_ICON_URL, COUNTRY_LANG_SELECTOR_DATA_GA} from "../../../../config/constants"
import {
    CountrySelectorContainer,
    CountrySelectorContainerHeader,
    CountrySelectorContainerElement,
    CountrySelectorCTAWrapper,
    LanguageButtonsContainer,
    LanguageButton,
    CloseButton,
    Title,
    CountrySelectorBottomText,
    StayButton,
    StyledIcon,
    StayText,
    CountrySelectorROW,
    CountrySelectorLink,
    DrawerContainer,
} from "./component"
import env from "../../../../config/env"
import type {DefaultTemplatePropType} from "../../index"
import {getAnchorDirection} from "../../util"

export const StandardCountryLangSelector = ({
    selectedCountry,
    selectedLanguage,
    selectLanguage,
    shopNowText,
    onCTAClick,
    headerTitle,
    closeText,
    closePanel,
    stayText,
    isOpeningFromBottom,
    isInternationalCountry,
    textAlignment,
    locationText,
    ROWText,
    ROWLinkText,
    ROWLinkUrl,
    languageText,
    isOpenCountrySelectorDrawer,
    keepMounted,
    disablePortal,
    hideBackdrop,
    disableScrollLock,
    isShowOverlay,
    territory,
}: DefaultTemplatePropType) => {
    const CHEVRON_ICON = `${env.APP_BLOB_STORAGE_PATH}${CHEVRON_ICON_URL}`
    const isMedUp = useMediaQuery(`(min-width:${breakpoints.values.md.toString()}px)`)
    const showDropdown = (selectedCountry?.Languages?.length || 0) > 2
    return (
        <DrawerContainer
            data-testid={formatTextTestIds(`header-drawer-country-selector`)}
            anchor={getAnchorDirection(isOpeningFromBottom)}
            open={isOpenCountrySelectorDrawer}
            onClose={closePanel}
            ModalProps={{keepMounted, disablePortal, hideBackdrop, disableScrollLock}}
            style={{position: isShowOverlay ? "fixed" : "initial"}}
        >
            <ClickAwayListener onClickAway={closePanel}>
                <CountrySelectorContainer data-testid={formatTextTestIds(`header-drawer-country-selector-container`)}>
                    <CountrySelectorContainerHeader>
                        <Title variant="subtitle2">{headerTitle}</Title>
                        <CloseButton disableRipple data-testid="country-selector-close-button" onClick={closePanel}>
                            {closeText}
                        </CloseButton>
                    </CountrySelectorContainerHeader>
                    <CountrySelectorContainerElement>
                        {locationText && <Typography>{locationText}</Typography>}
                        <CountrySelectDropdown />
                    </CountrySelectorContainerElement>
                    {ROWText && (
                        <CountrySelectorROW>
                            <Typography>{ROWText}</Typography>
                            <CountrySelectorLink
                                data-testid="country-selector-ROWLink"
                                href={ROWLinkUrl}
                                underline="always"
                            >
                                {ROWLinkText}
                            </CountrySelectorLink>
                        </CountrySelectorROW>
                    )}
                    <CountrySelectorContainerElement>
                        {languageText && <Typography>{languageText}</Typography>}
                        <LanguageButtonsContainer>
                            {/* eslint-disable-next-line react/display-name */}
                            {showDropdown ? (
                                <CountrySelectLanguageDropdown
                                    territory={territory}
                                    textAlignment={textAlignment}
                                    selectedCountry={selectedCountry}
                                    selectLanguage={selectLanguage}
                                    selectedLanguage={selectedLanguage}
                                />
                            ) : (
                                selectedCountry?.Languages.map((lan: CountryLanguage, i) => (
                                    <LanguageButton
                                        key={lan.AccountDomainUrl}
                                        selected={selectedLanguage === lan.Name}
                                        onClick={() => selectLanguage(lan.Name)}
                                        data-testid={`country-selector-language-button-${i}`}
                                        data-ga-v1={COUNTRY_LANG_SELECTOR_DATA_GA}
                                        data-ga-v2={selectedCountry.Country}
                                        data-ga-v3={lan.Value}
                                    >
                                        {lan.Value}
                                    </LanguageButton>
                                ))
                            )}
                        </LanguageButtonsContainer>
                    </CountrySelectorContainerElement>
                    <CountrySelectorCTAWrapper>
                        <CTA
                            text={shopNowText}
                            color="primary"
                            state="enabled"
                            variant="contained"
                            size={isMedUp ? "medium" : "large"}
                            onClick={onCTAClick}
                            testId="country-selector-CTA-button"
                        />
                    </CountrySelectorCTAWrapper>
                    {!isOpeningFromBottom && !isInternationalCountry && (
                        <CountrySelectorBottomText>
                            <StayButton onClick={closePanel} data-testid="country-selector-stay-text">
                                <StayText>{stayText}</StayText>
                                <StyledIcon textAlignment={textAlignment} src={CHEVRON_ICON} />
                            </StayButton>
                        </CountrySelectorBottomText>
                    )}
                </CountrySelectorContainer>
            </ClickAwayListener>
        </DrawerContainer>
    )
}

StandardCountryLangSelector.displayName = "StandardCountryLangSelector"

export default StandardCountryLangSelector
